/**
 * splitView.js — pure helpers for the vertical (side-by-side) split-screen outline (CT-024).
 *
 * No DOM, no React, no engine state — so the deep-link mapping and the divider clamp are
 * unit-testable in isolation (see splitView.test.mjs). The two consumers are:
 *   • outline.js   — Alt+click reads a node's data-* attributes → zoom object → iframe URL
 *   • SplitPane.jsx — clamps the draggable divider to the minimum pane sizes
 *
 * The right pane is just the app loaded in an iframe at the clicked node's `?z=` deep link (the
 * same scheme encodeZoom/applyEncodedZoom already use in outline.js), plus `split=1` so the
 * embedded copy skips the shared outline snapshot. We reuse the real outline — no parallel impl.
 */

// Drop null / undefined / '' so the encoded `z` payload only carries set fields (matches encodeZoom).
function clean(obj) {
  const out = {}
  Object.keys(obj).forEach(k => { if (obj[k]) out[k] = obj[k] })
  return out
}

// base64 that works in both the browser (btoa) and node (Buffer) — mirrors encodeZoom's btoa.
function b64(s) {
  try {
    if (typeof btoa === 'function') return btoa(s)
    if (typeof Buffer !== 'undefined') return Buffer.from(s, 'binary').toString('base64')
  } catch (e) { /* fall through */ }
  return ''
}

// A section key is `sec:<owner>:<type>…`. Mirror the `level==='section'` branch of the zoomTo
// dispatch in outline.js: derive the root + (phase|milestone) context from the owner segment.
function sectionObj(proj, sk) {
  const owner = (sk.split(':')[1]) || ''
  const o = { p: proj, sec: sk }
  if (owner === 'phase') { o.r = 'schedule'; o.ph = sk.split(':')[2] || null }
  else if (owner === 'threads') o.r = 'threads'
  else if (owner === 'meeting') o.r = 'meetings'
  else if (owner !== 'prod' && owner !== 'design' && owner !== 'construction' && owner !== 'permits') {
    // Section owned by a milestone — owner segment is the milestone record id.
    o.r = 'schedule'; o.ms = owner
  } else {
    o.r = owner === 'prod' ? 'production' : owner   // prod|design|construction|permits root section
  }
  return clean(o)
}

// Entity-detail zoom levels: each sets a root and a `<level>:<id>` section key.
// Mirrors the detail branches of the zoomTo dispatch in outline.js — keep in sync if levels change.
const DETAIL = {
  'meeting-detail':   ['meetings',     'data-meetingid'],
  'permit-detail':    ['permits',      'data-permitid'],
  'review-detail':    ['permits',      'data-reviewid'],
  'parcel-detail':    ['design',       'data-parcelid'],
  'campaign-detail':  ['construction', 'data-campaignid'],
  'bid-detail':       ['construction', 'data-bidid'],
  've-effort':        ['construction', 'data-veid'],
  've-item':          ['construction', 'data-veitemid'],
  'contract-detail':  ['construction', 'data-contractid'],
  'rfi-detail':       ['construction', 'data-rfiid'],
  'submittal-detail': ['construction', 'data-submittalid'],
  'asi-detail':       ['construction', 'data-asiid'],
  'bim-system-detail':  ['design', 'data-bimsystemid'],
  'bim-element-detail': ['design', 'data-bimelementid'],
}

/**
 * Map a clicked node's zoom bullet (its data-* attributes) to the deep-link zoom object
 * {p,r,ph,ms,sec,cat,it,cn} consumed by applyEncodedZoom. Mirrors the zoomTo dispatch in
 * outline.js (the single source of truth for level → zoom-state). Unknown levels fall back to
 * a project-rooted pane so a split always opens with something useful.
 *
 * @param {(name:string)=>?string} getAttr  reads a data attribute off the node's zoom element
 * @param {?string} projFallback  current project id, for nodes that don't carry data-projid
 */
export function zoomObjFromAttrs(getAttr, projFallback) {
  const lvl = getAttr('data-level') || ''
  const proj = getAttr('data-projid') || projFallback || null

  if (DETAIL[lvl]) {
    const [root, attr] = DETAIL[lvl]
    return clean({ p: proj, r: root, sec: lvl + ':' + (getAttr(attr) || '') })
  }

  switch (lvl) {
    case 'top':
    case 'top-concept':
      return {}                                                       // project-list / design root home
    case 'project':
      return clean({ p: getAttr('data-projid') })
    case 'project-home':
      return clean({ p: proj })
    case 'project-root':
      return clean({ p: getAttr('data-projid'), r: getAttr('data-rootname'), sec: getAttr('data-section') })
    case 'root':
      return clean({ p: proj, r: getAttr('data-rootname') })
    case 'phase':
      return clean({ p: proj, r: 'schedule', ph: getAttr('data-pid') })
    case 'ms':
      return clean({ p: proj, r: 'schedule', ph: getAttr('data-pid'), ms: getAttr('data-msid'), sec: getAttr('data-section') })
    case 'cat':
      return clean({ p: proj, r: 'schedule', ph: getAttr('data-pid'), ms: getAttr('data-msid'), cat: getAttr('data-cat') })
    case 'item':
      return clean({ p: proj, r: 'schedule', ph: getAttr('data-pid'), ms: getAttr('data-msid'), cat: getAttr('data-cat'), it: getAttr('data-itemid') })
    case 'concept':
      return clean({ p: proj, r: 'design', cn: getAttr('data-conceptid') })
    case 'concept-detail':
      return clean({ p: proj, r: 'design', sec: 'concept-detail:' + (getAttr('data-conceptid') || '') })
    case 'unit-detail':
      return clean({ p: proj, r: 'design', sec: 'unit-detail:' + (getAttr('data-unitid') || '') + ':' + (getAttr('data-conceptid') || '') })
    case 'level-detail':
      return clean({ p: proj, r: 'design', sec: 'level-detail:' + (getAttr('data-levelid') || '') + ':' + (getAttr('data-unitid') || '') })
    case 'section':
      return sectionObj(proj, getAttr('data-sectionkey') || '')
    default:
      return clean({ p: proj })                                       // unknown → project-rooted pane
  }
}

/**
 * Derive the zoom object directly from the live `wf` state — for the `/split` command, which has
 * no clicked node to read data-* attributes from. `wf`'s zoom fields map 1:1 onto the {p,r,ph,ms,
 * sec,cat,it,cn} shape (zoomProjectId→p, zoomRoot→r, zoomPhaseId→ph, zoomMsId→ms, zoomSection→sec,
 * zoomCat→cat, zoomItemId→it, zoomConceptId→cn), so this is a more direct — and more precise —
 * source of "what's on screen right now" than reconstructing from a DOM node ever could be.
 */
export function zoomObjFromWf(wf, projFallback) {
  return clean({
    p: wf.zoomProjectId || projFallback || null,
    r: wf.zoomRoot, ph: wf.zoomPhaseId, ms: wf.zoomMsId,
    sec: wf.zoomSection, cat: wf.zoomCat, it: wf.zoomItemId, cn: wf.zoomConceptId,
  })
}

/**
 * Fallback: derive the zoom object from a node's `data-key` when it has no zoom bullet element.
 * Covers the common key prefixes (proj:/ph:/ms:/root:/sec:). Anything else → project-rooted pane.
 */
export function zoomObjFromKey(key, projFallback) {
  if (!key) return clean({ p: projFallback })
  const i = key.indexOf(':')
  const type = i < 0 ? key : key.slice(0, i)
  const id = i < 0 ? '' : key.slice(i + 1)
  if (type === 'proj') return clean({ p: id })
  if (type === 'ph')   return clean({ p: projFallback, r: 'schedule', ph: id })
  if (type === 'ms')   return clean({ p: projFallback, r: 'schedule', ms: id })
  if (type === 'root') return clean({ p: projFallback, r: id })
  if (type === 'sec')  return sectionObj(projFallback, key)
  return clean({ p: projFallback })
}

/**
 * Build the iframe URL for the lower split pane: the clicked node's `?z=` deep link plus `split=1`.
 * `split=1` tells the embedded copy (state.js) to skip the shared outline snapshot so the two panes
 * stay independent. Returns an absolute URL when `origin` is supplied.
 */
export function buildSplitUrl(origin, obj) {
  obj = obj || {}
  const path = obj.p ? '/project' : '/'
  const params = []
  if (obj.p) params.push('recordId=' + encodeURIComponent(obj.p))
  const zsrc = {}
  ;['p', 'r', 'ph', 'ms', 'sec', 'cat', 'it', 'cn'].forEach(k => { if (obj[k]) zsrc[k] = obj[k] })
  // Only emit z when the target is deeper than the bare project (matches encodeZoom: '' when empty).
  if (obj.r || obj.ph || obj.ms || obj.sec || obj.cat || obj.it || obj.cn) {
    const z = b64(JSON.stringify(zsrc))
    if (z) params.push('z=' + encodeURIComponent(z))
  }
  // Rows to open on arrival (a peek/split pane starts with everything collapsed) — e.g. On Deck's
  // "Open" lands on the project's Schedule with one milestone's phase and the milestone expanded.
  // The last key is scrolled into view (outline.js reads `x`).
  if (Array.isArray(obj.x) && obj.x.length) params.push('x=' + encodeURIComponent(obj.x.join(',')))
  params.push('split=1')
  return (origin || '') + path + '?' + params.join('&')
}

/**
 * Clamp one pane's pixel size so neither pane drops below `minPane`. Axis-agnostic: pass heights
 * (horizontal split) or widths (vertical split). If the viewport is too small to honor both
 * minimums, fall back to an even split. `dividerH` is the divider's thickness.
 */
export function clampTopPx(topPx, viewportH, minPane, dividerH) {
  const max = viewportH - minPane - dividerH
  if (max < minPane) return Math.max(0, Math.round((viewportH - dividerH) / 2))
  return Math.min(max, Math.max(minPane, Math.round(topPx)))
}
